import React, { createContext, memo, useContext, useState, type FC, type ReactNode } from 'react';
import { Pressable, type ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

type RadioGroupContextValue = {
  value: string | null;
  onValueChange: (value: string) => void;
  disabled?: boolean;
};

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

function useRadioGroupContext() {
  const ctx = useContext(RadioGroupContext);
  if (!ctx) {
    throw new Error('RadioGroup.Radio must be used within <RadioGroup>.');
  }
  return ctx;
}

export type RadioGroupProps = {
  value?: string | null;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  direction?: 'row' | 'column';
  itemGap?: number;
  children: ReactNode;
  style?: ViewStyle | ViewStyle[];
};

const RadioGroupRoot: FC<RadioGroupProps> = ({
  value,
  onValueChange,
  disabled = false,
  direction = 'column',
  itemGap,
  children,
  style,
}) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(value ?? null);
  const { scale } = useScaling();
  const isControlled = value !== undefined;
  const _selectedValue = isControlled ? value : selectedValue;

  const handleValueChange = (_value: string) => {
    if (!isControlled) {
      setSelectedValue(_value);
    }
    onValueChange?.(_value);
  };

  return (
    <RadioGroupContext.Provider
      value={{ value: _selectedValue, onValueChange: handleValueChange, disabled }}>
      <Stack
        direction={direction}
        gap={direction !== 'column' ? (itemGap ?? scale(35)) : scale(4)}
        style={style}>
        {children}
      </Stack>
    </RadioGroupContext.Provider>
  );
};

export type RadioGroupItemProps = {
  value: string;
  label: string;
  /** Custom content to replace the label. */
  children?: ReactNode;
  style?: ViewStyle | ViewStyle[];
  color?: string;
};

const RadioGroupItem: FC<RadioGroupItemProps> = memo(({ value, label, children, style, color }) => {
  const { value: selectedValue, onValueChange, disabled } = useRadioGroupContext();
  const isSelected = selectedValue === value;
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Pressable
      onPress={() => !disabled && onValueChange(value)}
      disabled={disabled}
      style={[styles.row, disabled && styles.disabled, style]}
      accessibilityRole="radio"
      accessibilityState={{ checked: isSelected, disabled }}>
      {/* <Stack> */}
      {isSelected ? (
        <SvgIcon name="IcRadioChecked" size={scale(24)} color={colors['0']} />
      ) : (
        <SvgIcon name="IcRadioUnchecked" size={scale(24)} color={colors['0']} />
      )}
      {/* </Stack> */}
      {children ?? (
        <Text
          variant="Font Inter/Subtitle/Regular 12px"
          style={[styles.label, { color }]}
          numberOfLines={1}>
          {label}
        </Text>
      )}
    </Pressable>
  );
});

RadioGroupItem.displayName = 'RadioGroupItem';

export const RadioGroup = Object.assign(memo(RadioGroupRoot), {
  Radio: RadioGroupItem,
});

const useStyles = createStyles(({ scale }) => ({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
  },
  disabled: {
    opacity: 0.6,
  },
  label: {
    // flex: 1,
  },
}));
